import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  ContentType,
  PublicProfile,
} from "@dimensional-engineering/dimensional-models";

import styles from "./CompatibilitySnippets.module.css";

import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import {
  FriendsPublicProfile,
  WorkCompatibilityReport,
} from "@/models/sharedModels";
import LargeProfileImage from "@/components/shared/SmallProfileImage/LargeProfileImage";
import { RootState } from "@/redux/store";
import ComparedMediumProfileImage from "@/components/shared/SmallProfileImage/ComparedMediumProfileImage";
import { compatibilityIconsMap } from "./CompatibilityData";
import VibeWithIcon from "@/components/results/Snippets/snippetsIcons/VibeWithIcon";
import { LlmTitleAndContentMapper } from "./views/CompatibilityLlmTitleAndContentView";
import DontVibeWithIcon from "@/components/results/Snippets/snippetsIcons/DontVibeWithIcon";
import { CompatibilitySpectrumChart } from "./views/CompatibilitySpectrum";
import { useModalContext } from "@/context/ModalContext";
import {
  computeCompatibilitySnippets,
  getWorkCompatibilityFeedbackScore,
  getWorkCompatibilitySnippetLLMRequest,
  updateWorkCompatibilitFeedback,
} from "./utils";
import LightningStrikeIcon from "./icons/LightningStrikeIcon";
import AdviceIcon from "./icons/AdviceIcon";
import AnalysisFeedback from "@/components/results/personalityAnalysis/AnalysisFeedback/AnalysisFeedback";
import { useAuth } from "@/context/AuthContext";
import CompatibilityConversionCTA from "./CompatibilityConversionCTA";
import SnippetInterractionBar from "@/components/results/Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { isTimestampWithin1Day } from "@/components/authenticatedProfile/utils";
import Alert from "@/components/shared/Alerts/Alert";
import ConversionModal from "@/components/premium/ConversionModal/ConversionModal";
import LoadingScreen from "@/components/assessments-new/views/AsessmentOutroScreen/loadingScreen/LoadingScreen";
import { Mixpanel } from "@/helpers/mixpanel";
import InfoModal from "@/components/shared/InfoModal/InfoModal";
import {
  ListView,
  RomanticReportChapterHeader,
  RomanticReportHeader,
} from "./RomanticCompatibilityModal";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import HighBatteryIcon from "./icons/HighBatteryIcon";
import LowBatteryIcon from "./icons/LowBatteryIcon";
import PremiumPillIcon from "@/components/premium/PremiumPillIcon/PremiumPillIcon";
import { useAlertContext } from "@/context/AlertContext";

type Props = {
  workCompatibilityReport: WorkCompatibilityReport | null;
  publicProfile: PublicProfile | null;
};

export default function WorkCompatibilityModal(props: Props) {
  const userState = useSelector((state: RootState) => state.user);
  const connection = useConnectionList(props.publicProfile);
  const feedbackScore = useGetWorkFeedbackScore(connection?.ownerUUID);

  const { setModalComponent } = useModalContext();
  const { setAlertComponent } = useAlertContext();

  const { disabled, errorType } = useRegenerateStatus(
    props.publicProfile?.ownerUUID
  );

  const { user } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);

  const maxLength = 13;
  const myDisplayNAme = userState.publicProfileData?.userName || "";
  const thierDisplayNAme = connection?.userName || "";

  useEffect(() => {
    Mixpanel?.track(
      "Compatibility Snippet Viewed",
      {
        triggerer: userState.publicProfileData?.userName,
        triggeree: connection?.userName,
        compatibility_snippet_slug:
          "compatibility-snippet-work-compatibility-report",
      },
      user?.uid
    );
  }, []);

  function handleRegenerate() {
    if (!disabled) {
      if (props.publicProfile) {
        setModalComponent(
          <InfoModal
            title="Regenerate content?"
            body="You cannot undo this. Are you sure you want to regenerate this content?"
            secondaryText="Cancel"
            primaryText="Regenerate"
            onAccept={() => {
              setLoading(true);
              computeCompatibilitySnippets(
                "compatibility-snippet-work-compatibility-report",
                props.publicProfile?.ownerUUID as unknown as string
              )
                .then(() => {
                  Mixpanel?.track(
                    "Regenerate CTA Clicked",
                    {
                      content_type: "MP snippet",
                      slug: "compatibility-snippet-work-compatibility-report",
                    },
                    user?.uid
                  );
                })
                .catch((err) => {
                  setAlertComponent(
                    <Alert
                      type="fail"
                      message={
                        "Results can be regenerated once a day. Try again later."
                      }
                      elementName="Alert"
                    />,
                    7000
                  );
                  setLoading(false);
                });
            }}
            elementName="InfoModal"
          />
        );
      }
    } else {
      if (errorType === "premium") {
        return setModalComponent(<ConversionModal initialSlide={3} />);
      }
      setAlertComponent(
        <Alert
          type="fail"
          message={
            errorType === "time"
              ? "Results can be regenerated once a day. Try again later."
              : "Regenerating snippets is only available to Plus accounts"
          }
          elementName="Alert"
        />,
        7000
      );
    }
  }

  if (loading) {
    return (
      <ModalWrapper title="">
        <LoadingScreen message="Computing. We'll notify you when it's ready." />
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper title={"Work compatibility"} subTitle="Compatibility Snippet">
      <section
        style={{
          backgroundImage: `url(/compatibility-snippets/large/compatibility-snippet-romantic-compatibility-report.jpg)`,
        }}
        className={styles.reportTop}
      >
        <div className={styles.premiumIconReport}>
          <PremiumPillIcon />
        </div>
        <div className={styles.reportTopImagesDiv}>
          <div
            style={{ alignItems: "center" }}
            className={styles.generatePersonDiv}
          >
            <LargeProfileImage
              publicProfile={userState.publicProfileData}
              imageUrl={userState.imageURL ?? "/default-image.png"}
            />
            <p className={styles.generateUsername}>
              {myDisplayNAme.length <= maxLength
                ? `@${myDisplayNAme}`
                : `@${myDisplayNAme.slice(0, 11)}...`}
            </p>
          </div>

          <div className={styles.snippetIconSmallModal}>
            {
              compatibilityIconsMap[
                "compatibility-snippet-work-compatibility-report"
              ]
            }
          </div>

          <div
            style={{ alignItems: "center" }}
            className={styles.generatePersonDiv}
          >
            <ComparedMediumProfileImage
              large
              publicProfile={props.publicProfile}
              imageUrl={connection?.imageUrl ?? "/default-image.png"}
            />
            <p className={styles.generateUsername}>
              {thierDisplayNAme.length <= maxLength
                ? `@${thierDisplayNAme}`
                : `@${thierDisplayNAme.slice(0, 11)}...`}
            </p>
          </div>
        </div>
      </section>

      <section className={styles.modalContent}>
        <h1 className={styles.reportHeader}>Work compatibility</h1>
        <div className={styles.spectrumsParent}>
          <CompatibilitySpectrumChart
            connection={connection}
            spectrumSlug="compatibility-report-work-compatibility"
            contentType={ContentType.compatibilitySnippet}
          />
        </div>

        <RomanticReportHeader part={1} title="Overview" />
        <div className={styles.reportContentDiv}>
          <h3 className={styles.llmTitle}>Likely dynamic</h3>
          <p className={styles.llmParagraph}>
            {props.workCompatibilityReport?.likelyDynamicLlmString}
          </p>
          <h3 className={styles.llmTitle}>Your adjectives</h3>
          <ListView
            data={props.workCompatibilityReport?.llmVennDiagram}
            publicProfile={props.publicProfile}
          />
        </div>

        <RomanticReportHeader part={2} title="Individual summaries" />
        <div className={styles.individualSumamriesImageDiv}>
          <SmallProfileImage
            medium
            imageUrl={userState.imageURL ?? ""}
            publicProfile={userState.publicProfileData}
          />
          <h4 className={styles.usernameHeader}>
            @{userState.publicProfileData?.userName}
          </h4>
        </div>
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.mySummaryLlmContent}
          />
        </div>
        <div className={styles.individualSumamriesImageDiv}>
          <SmallProfileImage
            medium
            imageUrl={connection?.imageUrl ?? ""}
            publicProfile={connection}
          />
          <h4 className={styles.usernameHeader}>@{connection?.userName}</h4>
        </div>
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.connectionSummaryLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<LightningStrikeIcon />}
        />
        <RomanticReportHeader part={3} title="Your dynamic" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.yourDynamicLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<VibeWithIcon />}
        />
        <RomanticReportHeader part={4} title="Complementary traits" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.complementaryTraitsLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<DontVibeWithIcon />}
        />
        <RomanticReportHeader part={5} title="Incompatible traits" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.incompatibleTraitsLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<HighBatteryIcon />}
        />
        <RomanticReportHeader part={6} title="Strengths as a pair" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.pairStrengthsLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<LowBatteryIcon />}
        />
        <RomanticReportHeader part={7} title="Deficiencies as a pair" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.pairDeficienciesLlmContent}
          />
        </div>

        <RomanticReportChapterHeader
          publicProfile={props.publicProfile}
          icon={<AdviceIcon />}
        />
        <RomanticReportHeader part={8} title="Tips for success" />
        <div style={{ marginTop: "0" }} className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.myTipsForSuccess}
          />
        </div>
        <div className={styles.llmContentDivReport}>
          <LlmTitleAndContentMapper
            data={props.workCompatibilityReport?.connectionTipsForSuccess}
          />
        </div>

        <div className={styles.feedbackDiv}>
          <AnalysisFeedback
            onAnswer={(value) => {
              if (user?.uid && connection?.ownerUUID) {
                updateWorkCompatibilitFeedback(
                  user?.uid,
                  value,
                  connection?.ownerUUID
                );
              }
            }}
            title="How did you like this content?"
            subtitle="Feedback helps us improve"
            currentAnswer={feedbackScore}
          />
        </div>
      </section>
      <div className={styles.interactionBarDiv}>
        {!userState.isPremium && (
          <div className={styles.conversionDiv}>
            <CompatibilityConversionCTA username={connection?.userName} />
          </div>
        )}
        <SnippetInterractionBar
          hideShare
          onRegenerate={() => handleRegenerate()}
          disabledRegenerate={disabled}
          type="report"
          slug={"compatibility-snippet-work-compatibility-report"}
        />
      </div>
    </ModalWrapper>
  );
}

function useConnectionList(profile: PublicProfile | null) {
  const connectionListWithFriendDetails = useSelector(
    (state: RootState) => state.connections.publicProfiles
  );
  const [connection, setConnection] = useState<FriendsPublicProfile | null>(
    null
  );
  useEffect(() => {
    connectionListWithFriendDetails.forEach((f) => {
      if (f.ownerUUID === profile?.ownerUUID) {
        setConnection(f);
      }
    });
  }, [connectionListWithFriendDetails, profile?.ownerUUID]);

  return connection;
}

function useGetWorkFeedbackScore(connectionUid: string | undefined) {
  const [feedbackScore, setFeedbackScore] = useState<number | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.uid && connectionUid) {
      getWorkCompatibilityFeedbackScore(user?.uid, connectionUid)
        .then((res) => {
          setFeedbackScore(res);
        })
        .catch(() => {
          setFeedbackScore(null);
        });
    }
  }, [connectionUid, user?.uid]);

  return feedbackScore;
}

function useRegenerateStatus(connectionUid: string | undefined) {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<"premium" | "time">("premium");

  const { user } = useAuth();
  const isPremium = useSelector((state: RootState) => state.user.isPremium);

  useEffect(() => {
    if (!isPremium) {
      setDisabled(true);
      setErrorType("premium");
    } else {
      if (connectionUid) {
        getWorkCompatibilitySnippetLLMRequest(user?.uid, connectionUid)
          .then((res) => {
            const isWithin24Hours = isTimestampWithin1Day(res.createdOn);
            if (isWithin24Hours === true) {
              setDisabled(true);
              setErrorType("time");
            }
          })
          .catch((err) => {
            setDisabled(false);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, user?.uid]);

  return { disabled, errorType };
}
